import { AiMode, HistoryItem, RecentMatch, SwitchChange, WorkflowState } from '../../../api/cafAutomation';
import { PillTone } from '../copy';
import { formatCount, formatWhen } from '../format';

/** Every Automation sentence (spec §5). Plain UK English; no codes, IDs or ISO times. */

export const PAGE_SENTENCE = 'The AI decides, workflows do the work.';
export const OLD_BACKEND = 'Automation needs the latest server update. Until then the AI is switched on and off from Admin › Processors.';
export const FORWARD_RULE_USED_BY = 'Lender emails on cancelled claims';

export const MODE_LABEL: Record<AiMode, string> = { off: 'Off', watching: 'Watching', on: 'On' };

export const MODE_SENTENCE: Record<AiMode, string> = {
  off: "The AI isn't processing lender emails. They wait in the Lender lane for staff.",
  watching: 'The AI reads every lender email and shows what it would have done, including which workflow it would have '
    + "run. It doesn't do anything. Staff handle every task.",
  on: 'The AI processes lender emails one at a time. It follows your approved examples, runs their workflows, and '
    + "marks anything it isn't sure about as manual.",
};

export const SWITCH_SAVED: Record<AiMode, string> = {
  off: 'Saved. The AI is off.', watching: 'Saved. The AI is watching.', on: 'Saved. The AI is on.',
};

const emails = (n: number) => `${formatCount(n)} ${n === 1 ? 'email' : 'emails'}`;

export const liveLine = (mode: AiMode, waiting: number, lastAgo: string | null, paused: boolean): string => {
  let line = mode === 'off'
    ? `Paused by you · ${formatCount(waiting)} lender ${waiting === 1 ? 'email' : 'emails'} waiting in the Lender lane`
    : `${mode === 'on' ? 'Working' : 'Watching'} · ${emails(waiting)} waiting${lastAgo ? ` · last one ${lastAgo.charAt(0).toLowerCase()}${lastAgo.slice(1)}` : ''}`;
  if (paused) line += ' · all workflows paused by you';
  return line;
};

/**
 * Staff emails become names wherever a person is shown ("r.biggs@…" -> "R. Biggs"); a raw address is never shown.
 */
export const displayName = (email: unknown): string => {
  if (typeof email !== 'string') return 'Someone';
  const value = email.trim();
  // Already a name ("R. Biggs"): leave it.
  if (value && !value.includes('@') && /\s/.test(value)) return value.replace(/\s+/g, ' ');
  const local = value.split('@')[0];
  // Ids and system actors are not people.
  if (/^[0-9a-f-]{8,}$/i.test(local) || local.toLowerCase() === 'system') return 'Someone';
  const parts = local.split(/[._\-\s]+/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return 'Someone';
  return parts.map((part, index) => (index < parts.length - 1 && part.length === 1
    ? `${part.toUpperCase()}.`
    : part.charAt(0).toUpperCase() + part.slice(1))).join(' ');
};
export const personName = displayName;

export const changedLine = (change: SwitchChange | null, legacy: boolean): string => {
  if (!change) return legacy ? 'Carried over from before the update' : '';
  return `Changed by ${displayName(change.by)} · ${formatWhen(change.at)}${change.note ? ` · "${change.note}"` : ''}`;
};

export interface SwitchConfirmCopy { title: string; lines: string[]; button: string; danger: boolean }

export const switchConfirm = (context: {
  from: AiMode; to: AiMode; workflowsOn: number; waiting: number; paused: boolean;
  watched?: number; replacement?: boolean;
}): SwitchConfirmCopy => {
  const { from, to, workflowsOn, waiting, paused, watched = 0, replacement = false } = context;
  if (to === 'on') {
    if (replacement) {
      const lines = [
        `The AI will assess ${formatCount(waiting)} waiting ${waiting === 1 ? 'email' : 'emails'}, including ${formatCount(watched)} already watched. There is no age limit.`,
        `${formatCount(workflowsOn)} ${workflowsOn === 1 ? 'workflow is' : 'workflows are'} on. Only eligible emails for those workflows can start them; other decisions go to staff for manual handling.`,
        'Switching on does not turn on any other workflow.',
      ];
      if (paused) lines.push('All workflows are paused until you resume them.');
      return { title: 'Switch the AI on?', lines, button: 'Switch on', danger: false };
    }
    const lines = [workflowsOn > 0
      ? `The AI will start acting on lender emails, including sending client emails from the ${workflowsOn} ${workflowsOn === 1 ? 'workflow that is' : 'workflows that are'} on.`
      : 'The AI will start acting on lender emails.'];
    if (from === 'off') lines.push('Emails that arrived while it was off are processed too.');
    if (workflowsOn > 0) {
      lines.push(`That includes the ${emails(waiting)} waiting, however old they are: their workflows can run and email clients.`);
      if (paused) lines.push('All workflows are paused, so none will run until you resume them.');
    } else {
      lines.push('No workflows are on yet, so it uses its usual actions only.');
    }
    return { title: 'Switch the AI on?', lines, button: 'Switch on', danger: false };
  }
  if (to === 'watching') {
    const lines = ['The AI keeps reading emails but stops doing anything.'];
    if (from === 'on') lines.push('Workflows part-way through stop before their next step.');
    return { title: 'Switch the AI to watching?', lines, button: 'Switch to watching', danger: false };
  }
  const lines = ['The AI stops processing. New emails wait in the Lender lane for staff.'];
  if (from === 'on') lines.push('Workflows part-way through stop before their next step and carry on when the AI is back on.');
  return { title: 'Switch the AI off?', lines, button: 'Switch off', danger: true };
};

export const STATE_LABEL: Record<WorkflowState, string> = { draft: 'Draft', watching: 'Watching', on: 'On', paused: 'Paused' };
export const STATE_TONE: Record<WorkflowState, PillTone> = { draft: 'grey', watching: 'blue', on: 'teal', paused: 'amber' };

export const GATE_REASON: Record<string, string> = {
  no_hold: "The AI marked it as manual: it wasn't sure enough.",
  followed: "The AI didn't follow an example from this exact sender.",
  unanimous: 'The examples the AI looked at disagreed.',
  agrees: 'The AI followed a different example.',
  claim: "The AI couldn't match exactly one open claim.",
  lender_mail: "It wasn't an email from a lender.",
  backstops: "It didn't pass the safety checks for emailing the client.",
  linked: "The example's workflow wasn't watching or on.",
  workflow_matches: "The example's workflow doesn't do what the example says should happen.",
  first_decision: 'The email had already been acted on.',
  no_duplicate: 'This workflow already started for that claim in the last 21 days.',
};
export const gateSentence = (reason: string | null): string =>
  (reason && GATE_REASON[reason]) || "The workflow wouldn't start.";

export const DIFFERENCE: Record<string, string> = {
  other_claim: 'The built-in action attached a different claim.',
  no_send: "The built-in action didn't send the email.",
  other_ending: 'The built-in action ended differently.',
};

export const SEED_BLOCKED: Record<string, string> = {
  email_not_set_up: "Its client email isn't set up yet.",
  template_source_required: 'Import the configured template source to continue.',
  template_renderer_unsupported: 'Its source needs review and reimport with the current renderer.',
  template_token_contract: 'Its source and token mappings need review.',
  required_token_mapping_missing: 'Its source and token mappings need review.',
  unsafe_token_link: 'Its source links and token mappings need review.',
  protected_link_use_required: 'Its source must use the configured protected link.',
  send_preparation_key_missing: 'Protected email storage is unavailable. Ask an administrator to configure the existing shared protection key.',
  send_preparation_key_invalid: 'Protected email storage is unavailable. Ask an administrator to check the existing shared protection key.',
  template_source_unavailable: 'The stored source cannot be read. Ask an administrator to check protected email storage.',
  template_source_changed: 'The stored source failed its integrity check. Ask an administrator to inspect it.',
  template_changed: 'Its source changed and needs review.',
  template_remote_unavailable: 'The provider version cannot be checked. Reload or ask an administrator to check it.',

};
export const seedBlocked = (reason: string | null): string =>
  (reason && SEED_BLOCKED[reason]) || "Setup is unavailable. Reload or ask an administrator to check it.";

export const periodPhrase = (days: number): string => (days === 7 ? 'this week' : `in the last ${days} days`);

const SWITCH_WORDS: Record<AiMode, string> = { off: 'Switched off', watching: 'Switched to watching', on: 'Switched on' };
const STATE_WORDS: Record<string, string> = { paused: 'Paused', watching: 'Back to watching', on: 'Turned on' };
const WORKFLOW_WORDS: Record<string, string> = {
  started_watching: 'Started watching', saved: 'Saved a new version', version_saved: 'Saved a new version to approve',
  draft_saved: 'Saved as a draft', duplicated: 'Made as a copy', seeded: 'Set up as a built-in workflow', deleted: 'Deleted',
  rule_linked: 'Attached to lender emails on cancelled claims',
  pause_all: 'Paused all workflows', resume_all: 'Resumed workflows',
};

export const historyLine = (item: HistoryItem, workflowId?: string): string => {
  if (item.kind === 'switch' && item.mode) return SWITCH_WORDS[item.mode];
  if (item.kind === 'send_limit') {
    return `Changed how many client emails may go out at a time: ${item.from_limit ?? '?'} → ${item.to_limit ?? '?'}`;
  }
  if (item.kind === 'state_changed') return STATE_WORDS[String(item.details?.to_state)] || 'Changed';
  if (item.kind === 'linked') return item.details?.workflow_id === workflowId ? 'Attached to an example' : 'Removed from an example';
  return WORKFLOW_WORDS[item.kind] || 'Changed';
};

export const REFRESH_FAILED = "Couldn't refresh";
export const WORKING_LOADING = 'Updating…';
export const NOT_ENOUGH_EMAILS = 'Not enough emails yet';
export const NOT_ENOUGH_FINISHED_EMAILS = 'Not enough finished emails yet';
export const workingLoadFailed = (days: number): string => `Couldn't load the last ${days} days.`;

export const readinessSentence = (blocker: string): string => ({
  replacement_schema_not_ready: 'The server update is incomplete.',
  replacement_execution_not_installed: 'Workflow processing is not installed yet.',
  workflow_runner_disabled: 'Workflow processing is switched off on the server.',
  response_capture_disabled: 'Reply monitoring is switched off on the server.',
  response_source_not_configured: 'The reply mailboxes need setting up.',
  rag_disabled: 'The AI reading service is switched off.',
  rag_shadow_mode: 'The AI reading service is still in trial mode.',
}[blocker] || 'A server prerequisite needs checking before workflows can run.');

/** Why a saved decision's workflow could not run, for the evidence rows. */
export const readinessIssueSentence = (issue: string): string => ({
  final_response_workflow_invalid: 'Not run: the example is marked Final response, but this workflow would finish or email it.',
}[issue] || 'Not run: a check needs attention.');

/** A saved decision's own mode governs outcome copy; the current switch says nothing about history. */
export const decisionOutcomeLine = (item: RecentMatch): string => {
  if (item.switch_mode === 'watching') return item.disposition === 'manual'
    ? 'Watching · Manual handling needed; no business action taken.'
    : 'Watching · Proposed workflow; no business action taken.';
  if (item.switch_mode === 'on') {
    if (item.ending) return `On · Actual ending: ${item.ending}`;
    if (item.run) return 'On · Workflow started; no actual ending recorded.';
    if (item.disposition === 'manual') return 'On · Marked for manual handling; staff still need to handle this task.';
    return 'On · No workflow started.';
  }
  return 'Decision mode unavailable; no executed ending confirmed.';
};
